import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * `elevated` (default), `filled`, `outlined` and `text` reproduce Flutter's Material 3
   * ElevatedButton / FilledButton / OutlinedButton / TextButton as apps/mobile renders them.
   * The remaining variants are legacy and go away as each screen is rebuilt from its mobile twin.
   */
  variant?:
    | 'elevated'
    | 'filled'
    | 'outlined'
    | 'text'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'ghost'
    | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'elevated',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const legacySizeStyles = {
      sm: 'px-3 py-1.5 text-label-md',
      md: 'px-4 py-2.5 text-label-lg',
      lg: 'px-6 py-3 text-title-sm',
      icon: 'p-2',
    };

    const m3Variants = {
      elevated: 'bg-lm-surface-container-low text-lm-primary shadow-elevation-1 hover:shadow-elevation-2 active:shadow-elevation-1 disabled:bg-lm-on-surface/12',
      filled: 'bg-lm-primary text-lm-on-primary disabled:bg-lm-on-surface/12',
      outlined: 'bg-transparent text-lm-primary border border-lm-outline disabled:bg-transparent disabled:border-lm-on-surface/12',
      text: 'bg-transparent text-lm-primary disabled:bg-transparent',
    } as const;

    const legacyVariants = {
      primary: 'bg-lm-primary-container text-lm-on-primary-container hover:bg-lm-primary-container/90 active:bg-lm-primary-container/80',
      secondary: 'bg-lm-secondary-container text-lm-on-secondary-container hover:bg-lm-secondary-container/90 active:bg-lm-secondary-container/80',
      tertiary: 'bg-lm-tertiary-container text-lm-on-tertiary-container hover:bg-lm-tertiary-container/90 active:bg-lm-tertiary-container/80',
      ghost: 'bg-transparent text-lm-on-surface hover:bg-lm-surface-container-highest active:bg-lm-surface-container-high',
      destructive: 'bg-lm-error-container text-lm-on-error-container hover:bg-lm-error-container/90 active:bg-lm-error-container/80',
    } as const;

    const isMaterial3 = variant in m3Variants;
    const horizontalPadding = variant === 'text' ? 'px-3' : 'px-6';

    // Material 3 state layer: the label color at 8% on hover and 10% on focus/press.
    const m3Base = clsx(
      'relative overflow-hidden min-h-[40px] min-w-[64px] rounded-full text-label-lg',
      horizontalPadding,
      'before:pointer-events-none before:absolute before:inset-0 before:bg-current before:opacity-0 before:content-[\'\']',
      'hover:before:opacity-[0.08] focus-visible:before:opacity-10 active:before:opacity-10',
      'disabled:text-lm-on-surface/38 disabled:shadow-none disabled:before:opacity-0'
    );

    return (
      <button
        ref={ref}
        className={clsx(
          'inline-flex items-center justify-center gap-2 transition-all duration-200',
          'focus-visible:outline-none disabled:cursor-not-allowed',
          isMaterial3
            ? [m3Base, m3Variants[variant as keyof typeof m3Variants]]
            : [
                'font-medium rounded-button focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-lm-bg disabled:opacity-50',
                legacySizeStyles[size],
                legacyVariants[variant as keyof typeof legacyVariants],
              ],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg data-testid="button-spinner" className="animate-spin h-5 w-5 text-lm-on-surface" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="38 60" />
            </svg>
            <span className="sr-only">Loading...</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0" aria-hidden="true">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0" aria-hidden="true">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';